import angular from 'angular'
import jitterTime from 'jitter-time'
import { ApiUser, Role } from '@vend/types'

import map from 'lodash/map'
import find from 'lodash/find'
import uniq from 'lodash/uniq'
import filter from 'lodash/filter'
import flowRight from 'lodash/flowRight'
import { isEqual, some } from 'lodash'
import { fetchApiJson, trackError, userHasPermission } from '@vend/utilities'
import { $interval, $window } from '../ng-compat/imports'
import { EventEmitter } from '../util/event-emitter'
import { WEB_REGISTER } from '../components/web-register.constant'
import {
  FEATURE_ENABLE_CONTROL_ON_DISCOUNT,
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  InsufficientStateForOfflineError,
  MAX_DISCOUNT_PERCENT,
  MIN_DISCOUNT_PERCENT,
  PERMISSIONS,
  RULES,
} from '../constants'
import { usersStore } from '../factories/database/users-store'
import {
  posStateManager,
  webRegisterStatusManager,
} from '../ng-compat/managers'
import { getFeatureFromCache } from '../util/feature-cache'

const collectUnique = flowRight(uniq, filter, map)

const UPDATE_USER_INTERVAL = jitterTime(2)

const ACCOUNT_TYPES = {
  CASHIER: 'cashier',
  MANAGER: 'manager',
  ADMIN: 'admin',
}

// The user account_type with their privilege level
const PRIVILEGES = {
  [ACCOUNT_TYPES.CASHIER]: 0,
  [ACCOUNT_TYPES.MANAGER]: 1,
  [ACCOUNT_TYPES.ADMIN]: 2,
}

export interface LoggedInUserChangeEvent {
  loggedInUser: ApiUser
}

type Events = {
  loggedInUserChange: [LoggedInUserChangeEvent]
}

// TODO: LR-10798 Update Role type from @vend/types with the new rule structure
type Rule = {
  name: string
  config: { [key: string]: string | number }
}
type Permission = {
  name: string
}

export interface RolesConnection {
  roles: UserManagerRole[]
}

export type UserManagerRole = Role & {
  rules: Rule[]
  permissions: Permission[]
}

/**
 * Factory for managing users.
 *
 * @class UserManager
 * @constructor
 * @extends {EventEmitter}
 */
export class UserManager extends EventEmitter<Events> {
  private readonly _signoutUrl: string
  private _whenInitialised?: $TSFixMe

  constructor() {
    'ngInject'
    super()
    this._signoutUrl = '/signout?return=' + $window.encodeURI(WEB_REGISTER.URL)
  }

  /**
   * Initialises the user manager, retrieving the logged in user.
   *
   * @method init
   *
   * @return {Promise<UserManager>} A promise resolved with the user manager instance once initialisation is
   *         complete
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = this.fetchUserWithFallback()
        .then((user: ApiUser) => {
          this.setLoggedInUser(user)
          return this
        })
        .then(() => {
          $interval(() => this._updateLoggedInUser(), UPDATE_USER_INTERVAL)
        })
        .catch((error: Error) => {
          trackError('[UserManager] Failed to initialise user manager', {
            data: { error },
          })
          return null
        })
    }
    return this._whenInitialised
  }

  /**
   * Fetch the logged in user from the API, falling back to the POS state manager if the API request fails.
   * This method is used to ensure the user is fetched from the API when the app is online.
   * If the app is offline, the user is fetched from the POS state manager.
   * If the user is not found in the POS state manager, an error is thrown.
   * Similar to https://github.com/vend/webregister/blob/c0df639be5dbf302a2042ae956013fbd534ee403/assets/scripts/configs/active-user-config.ts
   */
  private fetchUserWithFallback() {
    return webRegisterStatusManager.init().then(() => {
      if (webRegisterStatusManager.isOnline()) {
        return this.fetchUser().catch(() => this.setUserFromPosState())
      }
      return this.setUserFromPosState()
    })
  }

  /**
   * Get the logged in user's details.
   *
   * @method getLoggedInUser
   *
   * @return {User} The logged in user's details
   */
  public getLoggedInUser(): ApiUser {
    const user = posStateManager.getUser()
    if (user) {
      return user
    }
    throw new Error('No logged in user found in posStateManager')
  }

  /**
   * Log out the current user - clearing the logged in user state from the POS state manager and redirecting to the
   * signout URL.
   *
   * @method logout
   */
  public logout() {
    this._clearUser()
    $window.location.assign(this._signoutUrl)
  }

  /**
   * Determine if a user has greater privileges than a second user.
   *
   * @method hasGreaterPrivilege
   *
   * @param {User} user1
   *        The user to check if it has greater privilege than the second.
   *
   * @param {User} user2
   *        The user to compare against.
   *
   * @return {Boolean} True of the first user has greater privileges than the second.
   */
  public hasGreaterPrivilege(user1: ApiUser, user2: ApiUser) {
    const privilegeUser1 = PRIVILEGES[user1.account_type]
    const privilegeUser2 = PRIVILEGES[user2.account_type]
    return privilegeUser1 > privilegeUser2
  }

  /**
   * Asserts whether the active user has a certain permission.
   *
   * @method hasPermission
   *``
   * @param {String} permissionName
   *        The name of the API permission to check
   *
   * @return {Boolean} True if a given user has permission
   */
  public hasPermission(permissionName: string) {
    // @todo: once we are confident about permissions being resolved appropriately for admins, remove `hasAdminAccess`
    return (
      this.hasAdminAccess() ||
      userHasPermission(this.getLoggedInUser(), permissionName)
    )
  }

  /**
   * Whether the current logged in user has admin access (i.e. is an admin).
   *
   * @method hasAdminAccess
   *
   * @return {Boolean} true if the logged in user has admin access, false otherwise
   */
  public hasAdminAccess() {
    return this._hasAccess(ACCOUNT_TYPES.ADMIN)
  }

  /**
   * Whether the given user can access the specified outlet
   *
   * @method canUserAccessOutlet
   *
   * @param {User} user
   *        The user to check if they have access to the outlet
   *
   * @param {String} outletId
   *        The id of the outlet to check if user can access
   *
   * @return {Boolean} true if the given user has access to the outlet id
   */
  public canUserAccessOutlet(user: ApiUser, outletId: string) {
    const canAccessAllOutlets =
      !user.restricted_outlet_ids || !user.restricted_outlet_ids.length
    return (
      canAccessAllOutlets || user.restricted_outlet_ids!.indexOf(outletId) > -1
    )
  }

  /**
   * Hydrates each item with a full user object within a collection.
   *
   * @method hydrateUsers
   *
   * @param  {Array} items
   *         The collection of items with a `user_id` property.
   *
   * @return {Promise<Array>} A promise resolved once each item has been hydrated with the full user.
   */
  public hydrateUsers(items: $TSFixMe[]) {
    const userIds = collectUnique(items, 'user_id')
    return Promise.all(map(userIds, userId => usersStore.get(userId))).then(
      users => {
        map(items, item => {
          item.user = find(users, { id: item.user_id })
        })
        return items
      }
    )
  }

  /**
   * Set the cached logged in user to the given user.
   *
   * @method setLoggedInUser
   *
   * @param  {User} user
   *         The user to set as the logged in user
   */
  public setLoggedInUser(user: ApiUser) {
    if (!user) {
      this.logout()
    }
    // Ensure we only pass a plain object to Redux, not an instance of a class (VdActiveUser)
    posStateManager.setUser(user && { ...user })
    usersStore.put(user)
    this.emit('loggedInUserChange', {
      loggedInUser: user,
    })
  }

  /**
   * Get a rule from the logged in user
   *
   * @method getRule
   *
   * @param {RULES} ruleName
   *        The name of the rule to check
   *
   * @return {Rule | null} rule if a given user has rule
   */
  public getRule(ruleName: RULES) {
    // TODO: LR-10032 change vend.js to use latest User type from @vend/types
    const user = this.getLoggedInUser()

    if (user?.rules?.hasOwnProperty(ruleName)) {
      return user.rules[ruleName]
    }

    return null
  }

  /**
   * Asserts whether the active user has a certain rule
   *
   * @method hasRule
   *
   * @param {RULES} ruleName
   *        The name of the rule to check
   *
   * @return {Boolean} if a given user has rule
   */
  public hasRule(ruleName: RULES) {
    return Boolean(this.getRule(ruleName))
  }

  /**
   * Asserts whether the active user has line item discount rule enabled
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * and will check the presence of `sale.discount.manage` permission or
   * `sale.line_item_discount.manage` rule
   *
   * @method canApplyLineItemDiscount
   *
   * @return {Boolean} if a given user has line item discount rule or sale discount permission enabled
   */
  public canApplyLineItemDiscount() {
    return getFeatureFromCache(FEATURE_ENABLE_CONTROL_ON_DISCOUNT)
      ? this.hasRule(RULES.LINE_ITEM_DISCOUNT)
      : this.hasPermission(PERMISSIONS.SALE_DISCOUNT)
  }

  /**
   * Asserts whether the active user has sale discount rule enabled
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * and will check the presence of `sale.discount.manage` permission or
   * `sale.total_sale_discount.manage` rule
   *
   * @method canApplySaleDiscount
   *
   * @return {Boolean} if a given user has sale discount rule or sale discount permission enabled
   */
  public canApplySaleDiscount() {
    return getFeatureFromCache(FEATURE_ENABLE_CONTROL_ON_DISCOUNT)
      ? this.hasRule(RULES.TOTAL_SALE_DISCOUNT)
      : this.hasPermission(PERMISSIONS.SALE_DISCOUNT)
  }

  /**
   * Returns the maximum sale discount allowed in percentage
   * based on the rule for the active user
   *
   * NOTE: This relies also on the `enable_control_on_discount` feature flag
   * defaults to 0 if feature flag is enabled
   *
   * @method getMaxSaleDiscountPercent
   *
   * @return {Number} The sale discount rule for the given user
   */
  public getMaxSaleDiscountPercent() {
    const saleDiscountRule = this.getRule(RULES.TOTAL_SALE_DISCOUNT)

    if (getFeatureFromCache(FEATURE_ENABLE_CONTROL_ON_DISCOUNT)) {
      // @ts-ignore
      return saleDiscountRule?.max_discount || MIN_DISCOUNT_PERCENT
    }

    return MAX_DISCOUNT_PERCENT
  }

  /**
   * Returns if a given role has sale discount rule or sale discount permission enabled
   * based on `enable_control_on_discount` feature flag value
   *
   * @method isRoleAllowedToApplyLineItemDiscount
   *
   * @return {Boolean} true if a given role has sale discount rule or sale discount permission enabled
   */
  public isRoleAllowedToApplySaleDiscount(role?: UserManagerRole) {
    if (!role) return false

    return getFeatureFromCache(FEATURE_ENABLE_CONTROL_ON_DISCOUNT)
      ? some(
          role.rules,
          value =>
            value.name === RULES.TOTAL_SALE_DISCOUNT &&
            !isEqual(value.config, {})
        )
      : some(
          role.permissions,
          value => value.name === PERMISSIONS.SALE_DISCOUNT
        )
  }

  /**
   * Returns if the logged in user has access to the sell screen
   *
   * @method hasAccessToSellScreen
   *
   * @return {Boolean}
   */
  public hasAccessToSellScreen() {
    const enable_register_access_control = getFeatureFromCache(
      FEATURE_ENABLE_REGISTER_ACCESS_CONTROL
    )
    if (!enable_register_access_control) {
      return true
    }

    return (
      this.hasPermission(PERMISSIONS.ACCESS) &&
      this.hasPermission(PERMISSIONS.SALE_PROCESS)
    )
  }

  // ============================== Private API ============================== //
  /**
   * Clears the logged in user stored in the POS state manager.
   *
   * @private
   * @method _clearUser
   *
   */
  private _clearUser() {
    posStateManager.setUser(null)
  }

  private setUserFromPosState() {
    const user = posStateManager.getUser()
    if (user) {
      this.setLoggedInUser(user)
    } else {
      throw new InsufficientStateForOfflineError('No logged in user found')
    }
  }

  private fetchUser() {
    return fetchApiJson('/api/2.0/user').then((resp: { data: ApiUser }) => {
      return resp.data
    })
  }

  private refetchUser() {
    return webRegisterStatusManager.init().then(() => {
      if (webRegisterStatusManager.isOnline()) {
        return this.fetchUser()
          .then(user => {
            this.setLoggedInUser(user)
          })
          .catch(() => this.setUserFromPosState())
      }
      return this.setUserFromPosState()
    })
  }

  /**
   * Update the current logged in user - fetching the logged in user from the API and updating the local state if
   * the user or the user's details have changed.
   *
   * @private
   * @method _updateLoggedInUser
   *
   * @return {Promise} A promise resolved once the logged in user has been updated
   */
  private _updateLoggedInUser() {
    return this.refetchUser().catch(() => this.logout())
  }

  /**
   * Determine whether the current logged in user has the given access level (or higher).
   *
   * @private
   * @method _hasAccess
   *
   * @param  {String} accountType
   *         Account type representing the access level being queried e.g. "admin", "manager" or "cashier"
   *
   * @return {Boolean} true if the logged in user has at least the access level of the given account type, false
   *         otherwise
   */
  private _hasAccess(accountType: string) {
    const user = this.getLoggedInUser()
    return user && PRIVILEGES[user.account_type] >= PRIVILEGES[accountType]
  }
}

angular.module('webregister.managers').service('userManager', UserManager)
